import { useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaFileAlt, FaCheck, FaSearch } from 'react-icons/fa';
import Navbar from './Navbar';
import Footer from './Footer';
import api from '../api/axios';

const QA = [
  { cat: 'General', q: 'Tell me about yourself.', a: 'Keep it to about a minute: your current role or studies, one or two achievements that matter for this job, and why you want this role. Example: "I am a sales executive with three years in B2B software. Last year I grew my territory revenue by 30%. I am now looking for a team where I can take on larger accounts, which is why this role interests me."' },
  { cat: 'General', q: 'Why do you want to work here?', a: 'Name something specific about the company (a product, its customers, its growth) and connect it to your skills. Avoid generic answers like "it is a good company". Show you have read the job description and can explain how you will add value.' },
  { cat: 'General', q: 'Where do you see yourself in five years?', a: 'Show ambition that fits the role. Example: "I want to build deep expertise in this field, take on more responsibility, and eventually mentor others. I see this role as the right place to grow toward that."' },
  { cat: 'General', q: 'Why are you leaving your current job?', a: 'Stay positive and forward-looking. Talk about what you want next (growth, new challenges, a better fit), not complaints about your employer. Example: "I have learned a lot, and I am now looking for bigger responsibility than my current team can offer."' },
  { cat: 'Strengths', q: 'What are your strengths?', a: 'Pick two or three strengths relevant to the job and back each with a short example. Example: "I am organised. When our team missed deadlines, I built a simple tracker that cut delays by half."' },
  { cat: 'Strengths', q: 'What is your biggest weakness?', a: 'Choose a real but manageable weakness, and show how you are improving it. Example: "I used to take on too much myself. I now delegate and set clear priorities, and my last project finished on time because of it."' },
  { cat: 'Behavioural', q: 'Describe a time you faced a difficult problem at work.', a: 'Use the STAR method: Situation, Task, Action, Result. Describe the context briefly, what you were responsible for, the exact steps you took, and a measurable result. Spend most of your time on the action and result.' },
  { cat: 'Behavioural', q: 'Tell me about a time you disagreed with a colleague or manager.', a: 'Show that you stay respectful and focus on the goal. Explain how you listened, shared your reasoning with facts, and reached a solution together. End with what you learned.' },
  { cat: 'Behavioural', q: 'Describe a time you failed and what you learned.', a: 'Pick a real but low-stakes failure, take responsibility without blaming others, and focus on what you changed afterwards. Interviewers want to see self-awareness and learning.' },
  { cat: 'Behavioural', q: 'How do you handle pressure and tight deadlines?', a: 'Explain your method: list tasks, prioritise by impact, communicate early if something is at risk, and stay calm. Add a short example where you delivered on time under pressure.' },
  { cat: 'Freshers', q: 'You have no experience. Why should we hire you?', a: 'Focus on skills, attitude and proof: projects, internships, college work or certifications. Example: "I may be new, but I learn fast. My final-year project taught me to work in a team and meet deadlines, and I am eager to apply that here."' },
  { cat: 'Freshers', q: 'What did you learn from your final-year project or internship?', a: 'Describe the problem, your role, the tools you used and the result. Mention one thing you would do differently. Keep it specific and honest.' },
  { cat: 'Salary', q: 'What are your salary expectations?', a: 'Research the market range first. Give a range rather than one number, and say you are flexible for the right role. Example: "Based on my research and experience, I expect between X and Y, and I am open to discussing the full package."' },
  { cat: 'Salary', q: 'Do you have any questions for us?', a: 'Always ask something. Good questions: "What does success look like in the first 90 days?", "How is the team structured?", "What are the biggest challenges for this role?" Avoid asking only about salary and leave.' },
];

const CATS = ['All', ...Array.from(new Set(QA.map((x) => x.cat)))];

export default function GuestInterviewQA() {
  const fileRef = useRef(null);
  const [cat, setCat] = useState('All');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(0);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(false);
  const [role, setRole] = useState('');
  const [personal, setPersonal] = useState([]);

  const list = useMemo(() => {
    const s = query.trim().toLowerCase();
    return QA.filter((x) => (cat === 'All' || x.cat === cat) && (!s || x.q.toLowerCase().includes(s) || x.a.toLowerCase().includes(s)));
  }, [cat, query]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    setError('');
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) { setFile(null); setError('Please upload a PDF or DOCX file.'); return; }
    if (f.size > 5 * 1024 * 1024) { setFile(null); setError('File is too large. Maximum size is 5 MB.'); return; }
    setFile(f);
  };

  const generate = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('resume', file);
      form.append('difficulty', 'normal');
      const res = await api.post('/guest/mock-interview/start', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPersonal(res.data.questions || []);
      setRole(res.data.target_role || '');
    } catch (err) {
      if (err.response?.status === 429) {
        setLimit(true);
        setError('You have reached the free limit for now. Register to keep going.');
      } else {
        setError(err.response?.data?.errors?.resume?.[0] || err.response?.data?.message || 'Could not read your resume. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const btn = 'text-white px-6 py-3 rounded-xl font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(124,58,237,0.6)] disabled:opacity-50 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] transition hover:-translate-y-0.5';

  return (
    <div className="min-h-screen bg-[#FBF9FE] overflow-x-clip text-[#1E0F3D]">
      <Navbar />

      <header className="bg-gradient-to-br from-[#FBF5FF] via-white to-[#FDF0F6] border-b border-purple-100">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10 text-center">
          <p className="inline-block text-xs font-semibold text-[#7C3AED] bg-white border border-purple-100 rounded-full px-3 py-1.5 mb-4">Free, no login needed</p>
          <h1 className="mx-auto w-fit max-w-full text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight bg-gradient-to-r from-[#5B21B6] via-[#BE185D] to-[#5B21B6] bg-clip-text text-transparent">
            Interview Questions and Answers
          </h1>
          <p className="max-w-2xl mx-auto mt-3 text-base leading-7 font-medium text-[#1E0F3D]">
            Practise the questions interviewers ask most, with sample answers. Upload your resume to get questions based on your own experience.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-8 space-y-10">
        {/* resume-based */}
        <section className="rounded-3xl bg-white border border-purple-100 p-6 shadow-[0_20px_50px_-30px_rgba(124,58,237,0.45)]">
          <h2 className="text-xl font-bold mb-1">Questions from your resume</h2>
          <p className="text-sm text-gray-600 mb-4">Upload a PDF or DOCX (up to 5 MB). Your file is not saved.</p>

          <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={pick} className="hidden" />
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-dashed border-[#D8C8F7] bg-[#FBF5FF] text-sm font-semibold hover:border-[#7C3AED]">
              {file ? <FaCheck className="text-[#10B981]" /> : <FaFileAlt className="text-[#7C3AED]" />}
              <span className="break-all">{file ? file.name : 'Choose your resume'}</span>
            </button>
            {limit ? (
              <Link to="/register" className={`${btn} inline-block`}>Register to continue</Link>
            ) : (
              <button type="button" onClick={generate} disabled={!file || loading} className={btn}>
                {loading ? 'Reading your resume...' : 'Get my questions'}
              </button>
            )}
          </div>
          {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

          {personal.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold mb-3">{role ? `Questions for ${role}` : 'Your questions'}</p>
              <ol className="space-y-3">
                {personal.map((p, i) => (
                  <li key={i} className="flex gap-3 rounded-2xl border border-purple-100 bg-[#FBF5FF] px-4 py-3">
                    <span className="w-7 h-7 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white text-xs font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div>
                      <p className="font-semibold text-sm">{p.question}</p>
                      {p.category && <p className="text-xs text-gray-500 mt-0.5">{p.category}</p>}
                    </div>
                  </li>
                ))}
              </ol>
              <p className="text-sm text-gray-600 mt-4">
                Want feedback on your answers? <Link to="/mock-interview" className="font-semibold text-[#7C3AED] hover:underline">Try the free mock interview</Link> or <Link to="/login" className="font-semibold text-[#DB2777] hover:underline">log in</Link> for full interview prep.
              </p>
            </div>
          )}
        </section>

        {/* common Q&A */}
        <section>
          <h2 className="text-xl font-bold mb-4">Common interview questions</h2>

          <div className="relative mb-4">
            <label htmlFor="qa-search" className="sr-only">Search questions</label>
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" aria-hidden="true" />
            <input
              id="qa-search" type="search" value={query} onChange={(e) => { setQuery(e.target.value); setOpen(0); }}
              placeholder="Search questions"
              className="w-full rounded-full border border-purple-100 bg-white pl-11 pr-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {CATS.map((c) => (
              <button
                key={c} type="button" onClick={() => { setCat(c); setOpen(0); }}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${cat === c ? 'bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white shadow-md' : 'bg-white border border-purple-100 text-[#7C3AED] hover:bg-[#F5F0FA]'}`}
              >
                {c}
              </button>
            ))}
          </div>

          {list.length === 0 && (
            <p className="rounded-2xl bg-white border border-purple-100 px-6 py-8 text-center text-sm text-gray-600">No questions match “{query}”.</p>
          )}

          <div className="space-y-3">
            {list.map((x, i) => {
              const isOpen = open === i;
              return (
                <div key={x.q} className={`bg-white border rounded-2xl overflow-hidden transition-all ${isOpen ? 'border-[#DB2777]/40 shadow-[0_20px_40px_-22px_rgba(124,58,237,0.45)]' : 'border-purple-100'}`}>
                  <button
                    type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50"
                  >
                    <span>
                      <span className="block text-xs font-semibold text-[#7C3AED] mb-0.5">{x.cat}</span>
                      {x.q}
                    </span>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xl leading-none shrink-0 transition-all duration-300 ${isOpen ? 'rotate-45 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white' : 'bg-[#F5F0FA] text-[#DB2777]'}`} aria-hidden="true">+</span>
                  </button>
                  {isOpen && <p className="px-5 pb-5 text-sm text-gray-700 leading-relaxed">{x.a}</p>}
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-3xl bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-7 py-8 text-center">
          <h2 className="text-2xl font-bold mb-1">Ready to practise out loud?</h2>
          <p className="text-white/90 text-sm mb-5">Answer 3 questions from your resume and get a score on each answer.</p>
          <Link to="/mock-interview" className="inline-block bg-white text-[#7C3AED] px-7 py-3 rounded-xl font-bold text-sm transition hover:-translate-y-0.5">Start free mock interview</Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}